import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';
import type { Extension } from '@arcnet/contracts';

/**
 * Call screening: who is refused before a phone rings.
 *
 * Two places, one list of rules. A caller blocked for the whole account is the
 * account's Blocked numbers; a caller blocked for one person is on that
 * person's extension, where they would look for it. The opposite direction,
 * what a seat may DIAL, is Dialling restrictions.
 */

interface Rule {
  id: string;
  extension_id: string | null;
  extension_number: string | null;
  match_type: 'number' | 'prefix' | 'anonymous';
  pattern: string | null;
  action: 'reject' | 'voicemail' | 'silence';
  label: string | null;
  is_enabled: boolean;
  hit_count: number;
  last_hit_at: string | null;
}

const ACTION_LABEL: Record<Rule['action'], string> = {
  reject: 'Refused',
  voicemail: 'Straight to voicemail',
  silence: 'Answered in silence',
};

/** The account-wide block list. */
export function ScreeningTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Blocked numbers</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Callers refused before any phone on the account rings. Blocking a caller for one
            person is on that person's extension, under Screening.
          </p>
        </div>
      </div>
      <ScreeningRules tenantId={tenantId} writable={can('extension:write')} />
    </>
  );
}

/**
 * One extension's own screening. Not a paid feature -- the product catalogue has
 * no screening entitlement -- so every seat can screen its own callers.
 */
export function ExtensionScreeningTab({ tenantId, ext }: {
  tenantId: string; ext: Extension; scope: 'platform' | 'tenant';
}) {
  const can = useSession((s) => s.can);
  return <ScreeningRules tenantId={tenantId} extensionId={ext.id} writable={can('extension:write')} />;
}

function ScreeningRules({ tenantId, extensionId, writable }: {
  tenantId: string;
  /** Absent: the account-wide rules. */
  extensionId?: string;
  writable: boolean;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const key = ['tenants', tenantId, 'screening', extensionId ?? 'account'] as const;
  const rules = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ data: Rule[] }>(
      `/v1/tenants/${tenantId}/screening?${extensionId ? `extension_id=${extensionId}` : 'scope=account'}`),
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: key });

  const toggle = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      api.patch(`/v1/tenants/${tenantId}/screening/${id}`, { is_enabled: on }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/screening/${id}`),
    onSuccess: invalidate,
  });

  if (rules.error) return <ErrorBanner error={rules.error} />;
  const rows = rules.data?.data ?? [];

  return (
    <>
      {adding && (
        <RuleEditor tenantId={tenantId} {...(extensionId ? { extensionId } : {})}
          onCancel={() => setAdding(false)}
          onDone={() => { setAdding(false); invalidate(); }} />
      )}

      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">{extensionId ? 'Callers screened for this extension' : 'Blocked for the whole account'}</h2>
          <div className="arc-spacer" />
          {writable && !adding && (
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setAdding(true)}>
              <Icon name="plus" size={12} /> Block a caller
            </button>
          )}
        </div>
        {rules.isLoading ? (
          <TableSkeleton rows={4} cols={4} />
        ) : rows.length === 0 ? (
          <Empty icon="warning" title="Nobody is screened"
            hint="Block a specific number, a whole prefix, or callers who withhold their number." />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Caller</th><th>What happens</th><th>Blocked</th>
                  {writable && <th style={{ width: 90 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={r.is_enabled ? undefined : { opacity: 0.55 }}>
                    <td>
                      <div style={{ fontWeight: 550 }}>
                        {r.match_type === 'anonymous'
                          ? 'Withheld / anonymous'
                          : <span className="arc-mono">
                              {r.pattern}{r.match_type === 'prefix' ? '…' : ''}
                            </span>}
                      </div>
                      {r.label && <div className="arc-subtle" style={{ marginTop: 2 }}>{r.label}</div>}
                    </td>
                    <td>{ACTION_LABEL[r.action]}</td>
                    <td className="arc-subtle">
                      {/* A rule that has never fired is one somebody can delete;
                          the count is what makes that judgement possible. */}
                      {r.hit_count > 0
                        ? <>{r.hit_count}× {r.last_hit_at && <>· {relativeTime(r.last_hit_at)}</>}</>
                        : 'never'}
                    </td>
                    {writable && (
                      <td>
                        <div className="arc-row" style={{ gap: 6 }}>
                          <Switch id={`sc-${r.id}`} checked={r.is_enabled}
                            disabled={toggle.isPending}
                            onChange={(on) => toggle.mutate({ id: r.id, on })} />
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            title="Delete" onClick={() => remove.mutate(r.id)}>
                            <Icon name="trash" size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(toggle.error ?? remove.error) != null && (
        <ErrorBanner error={toggle.error ?? remove.error} />
      )}
    </>
  );
}

function RuleEditor({ tenantId, extensionId, onCancel, onDone }: {
  tenantId: string;
  /** Set: a rule for this extension only. Absent: for the whole account. */
  extensionId?: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [matchType, setMatchType] = useState<Rule['match_type']>('number');
  const [pattern, setPattern] = useState('');
  const [action, setAction] = useState<Rule['action']>('reject');
  const [label, setLabel] = useState('');

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/v1/tenants/${tenantId}/screening`, body),
    onSuccess: () => onDone(),
  });

  const needsPattern = matchType !== 'anonymous';
  const digits = pattern.replace(/\D/g, '');

  return (
    <div className="arc-card" style={{ marginBottom: 12 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">{extensionId ? 'Block a caller for this extension' : 'Block a caller for the whole account'}</h2>
      </div>
      <div className="arc-card-body">
        <div className="arc-grid arc-grid-2" style={{ gap: 10 }}>
          <label className="arc-field">
            <span>Match</span>
            <select className="arc-select" value={matchType}
              onChange={(e) => setMatchType(e.target.value as Rule['match_type'])}>
              <option value="number">One number</option>
              <option value="prefix">Everything starting with…</option>
              <option value="anonymous">Withheld / anonymous callers</option>
            </select>
          </label>
          {needsPattern ? (
            <label className="arc-field">
              <span>{matchType === 'prefix' ? 'Prefix' : 'Number'}</span>
              <input className="arc-input arc-mono" value={pattern} inputMode="tel"
                placeholder={matchType === 'prefix' ? '27861' : '+27 82 123 4567'}
                onChange={(e) => setPattern(e.target.value)} />
              {/* Pasted straight from a call log, so spacing and + are fine. */}
              <div className="arc-hint">
                {digits.length >= 2
                  ? <>Stored as <span className="arc-mono">{digits}</span></>
                  : 'Spaces and + are fine — they are stripped.'}
              </div>
            </label>
          ) : (
            <div className="arc-field">
              <span className="arc-label">Number</span>
              <p className="arc-subtle" style={{ margin: '6px 0 0' }}>
                Matches callers who present no number at all.
              </p>
            </div>
          )}
        </div>

        <div className="arc-grid arc-grid-2" style={{ gap: 10, marginTop: 10 }}>
          <label className="arc-field">
            <span>What happens</span>
            <select className="arc-select" value={action}
              onChange={(e) => setAction(e.target.value as Rule['action'])}>
              <option value="reject">Refuse the call</option>
              {/* A mailbox belongs to a seat, so only a seat's own rule can use one. */}
              {extensionId && <option value="voicemail">Send to voicemail</option>}
              <option value="silence">Answer in silence</option>
            </select>
            <div className="arc-hint">
              {action === 'reject' && 'The caller hears a rejection — they know they were blocked.'}
              {action === 'voicemail' && 'The caller reaches this extension’s voicemail and it never rings.'}
              {action === 'silence' && 'The caller learns nothing: no ring, no tone, no message.'}
            </div>
          </label>
          <label className="arc-field">
            <span>Note</span>
            <input className="arc-input" value={label} placeholder="Persistent cold caller"
              onChange={(e) => setLabel(e.target.value)} />
          </label>
        </div>

        {create.error != null && <ErrorBanner error={create.error} />}

        <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="arc-btn arc-btn-primary"
            disabled={create.isPending || (needsPattern && digits.length < 2)}
            onClick={() => create.mutate({
              match_type: matchType,
              ...(needsPattern ? { pattern: digits } : {}),
              action,
              ...(extensionId ? { extension_id: extensionId } : {}),
              ...(label.trim() ? { label: label.trim() } : {}),
            })}>
            {create.isPending ? 'Saving…' : 'Block caller'}
          </button>
        </div>
      </div>
    </div>
  );
}
